import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';

import { fetchAllPostCandidates } from 'api/aiClipperPost/client';
import {
  PostAiClipCandidate,
  PostCandidateStatus,
} from 'api/aiClipperPost/types';
import {
  getPostCandidateOwnerId,
  getPostCandidateSourceMediaPostId,
} from 'api/aiClipperPost/helpers';
import { AI_POST_CLIP_DETAIL_GEN } from 'constants/routes';
import {
  ATHLETE_DEBOUNCE_MS,
  useDebouncedValue,
} from 'pages/AIClipperMetrics/helpers';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const STATUS_OPTIONS: { value: PostCandidateStatus; label: string }[] = [
  { value: PostCandidateStatus.NotReviewed, label: 'Pending' },
  { value: PostCandidateStatus.Accepted, label: 'Accepted' },
  { value: PostCandidateStatus.Rejected, label: 'Rejected' },
];

interface SourceGroup {
  sourceMediaPostId: string;
  sourcePostTitle: string | null;
  sourcePostSlug: string | null;
  ownerId: string;
  ownerSlug: string | null;
  ownerName: string | null;
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
  generatedAt: string;
}

function groupBySource(candidates: PostAiClipCandidate[]): SourceGroup[] {
  const map = new Map<string, SourceGroup>();
  for (const c of candidates) {
    const key = getPostCandidateSourceMediaPostId(c) ?? c.id;
    const existing = map.get(key);
    const ownerId = getPostCandidateOwnerId(c);
    if (!existing) {
      map.set(key, {
        sourceMediaPostId: key,
        sourcePostTitle: c.sourceMediaPost?.title ?? null,
        sourcePostSlug: c.sourceMediaPost?.slug ?? null,
        ownerId: ownerId ?? '—',
        ownerSlug: c.sourceMediaPost?.store?.slug ?? null,
        ownerName:
          [
            c.sourceMediaPost?.store?.firstName,
            c.sourceMediaPost?.store?.lastName,
          ]
            .filter(Boolean)
            .join(' ') || null,
        total: 1,
        pending:
          c.postCandidateStatus === PostCandidateStatus.NotReviewed ? 1 : 0,
        accepted:
          c.postCandidateStatus === PostCandidateStatus.Accepted ? 1 : 0,
        rejected:
          c.postCandidateStatus === PostCandidateStatus.Rejected ? 1 : 0,
        generatedAt: c.createdAt,
      });
    } else {
      existing.total += 1;
      if (c.postCandidateStatus === PostCandidateStatus.NotReviewed)
        existing.pending += 1;
      if (c.postCandidateStatus === PostCandidateStatus.Accepted)
        existing.accepted += 1;
      if (c.postCandidateStatus === PostCandidateStatus.Rejected)
        existing.rejected += 1;
      if (c.createdAt > existing.generatedAt)
        existing.generatedAt = c.createdAt;
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    b.generatedAt.localeCompare(a.generatedAt)
  );
}

const PostClipsTable: React.FC = () => {
  const apolloClient = useApolloClient();
  const history = useHistory();

  const [statuses, setStatuses] = useState<PostCandidateStatus[]>([]);
  const [query, setQuery] = useState('');
  const [range, setRange] = useState<[Moment | null, Moment | null] | null>(
    null
  );

  const [candidates, setCandidates] = useState<PostAiClipCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebouncedValue(query, ATHLETE_DEBOUNCE_MS);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const entities = await fetchAllPostCandidates(apolloClient, {
        statuses: statuses.length ? statuses : undefined,
        query: debouncedQuery.trim() || undefined,
        fromDate: range?.[0]?.clone().startOf('day').toISOString(),
        toDate: range?.[1]?.clone().endOf('day').toISOString(),
      });
      setCandidates(entities);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load post clips.');
    } finally {
      setLoading(false);
    }
  }, [apolloClient, statuses, debouncedQuery, range]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => groupBySource(candidates), [candidates]);

  const columns = [
    {
      title: 'Source',
      key: 'source',
      render: () => <Tag color="blue">Post</Tag>,
    },
    {
      title: 'Source post',
      key: 'sourcePost',
      render: (_: unknown, row: SourceGroup) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.sourcePostTitle || '(untitled)'}</Text>
          {row.sourcePostSlug && (
            <Text type="secondary">{row.sourcePostSlug}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Owner',
      key: 'owner',
      render: (_: unknown, row: SourceGroup) => (
        <Space direction="vertical" size={0}>
          <Text>{row.ownerName || row.ownerSlug || row.ownerId}</Text>
          {row.ownerSlug && <Text type="secondary">@{row.ownerSlug}</Text>}
        </Space>
      ),
    },
    { title: 'Clips', dataIndex: 'total', key: 'total' },
    {
      title: 'Pending / Accepted / Rejected',
      key: 'counts',
      render: (_: unknown, row: SourceGroup) => (
        <Space>
          <Tag color="orange">{row.pending}</Tag>
          <Tag color="green">{row.accepted}</Tag>
          <Tag color="red">{row.rejected}</Tag>
        </Space>
      ),
    },
    {
      title: 'Generated',
      dataIndex: 'generatedAt',
      key: 'generatedAt',
      render: (v: string) => moment(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: unknown, row: SourceGroup) => (
        <Button
          type="link"
          onClick={() =>
            history.push(`${AI_POST_CLIP_DETAIL_GEN}/${row.sourceMediaPostId}`)
          }
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Owner slug or name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={load}
          allowClear
          style={{ width: 240 }}
        />
        <Select<PostCandidateStatus[]>
          mode="multiple"
          placeholder="Status"
          value={statuses}
          onChange={(next) => setStatuses(next ?? [])}
          options={STATUS_OPTIONS}
          style={{ minWidth: 200 }}
          allowClear
          maxTagCount="responsive"
        />
        <RangePicker
          value={range as any}
          onChange={(next) =>
            setRange(next as [Moment | null, Moment | null] | null)
          }
          allowClear
        />
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </Space>

      <Table<SourceGroup>
        rowKey="sourceMediaPostId"
        size="small"
        loading={loading}
        dataSource={groups}
        columns={columns}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 20 }}
        locale={{ emptyText: 'No post clips for the current filter.' }}
      />
    </>
  );
};

export default PostClipsTable;
